import React, { useState, useRef } from 'react';
import { X, Key, Camera, Linkedin, PenLine, CheckCircle, Shield, ArrowRight, AlertTriangle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useUser } from '../../contexts/UserContext';
import { useModalDismiss } from '../../hooks/useModalDismiss';
import { DSP_CRITERION, isDspRole } from '../../lib/roleLabels';

const ALLOWED_MIME = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_FILE_BYTES = 5 * 1024 * 1024; // 5MB
// ⚠️ Mirrors 061's cap on verification_queue.applicant_note (RAISEs past it).
export const VERIFICATION_NOTE_MAX = 1000;
// Methods an admin reviews: each carries the "tell us about your work" note
// and the attestation tick (061). An invite code doesn't — an admin already
// decided when they marked the code.
const REVIEWED_METHODS = ['badge', 'linkedin', 'attestation'];

// Every method ends with an admin's decision: a badge or LinkedIn goes to
// /admin/verification, and an invite code verifies only if an admin marked it
// to (058). Agency email (instant, no review) is off — a whitelisted domain
// covers everyone at the agency, CEO included — and send-verification-email
// refuses it server-side. Member Vouch is gone until something exists to
// review a vouch; it only ever set 'pending'. 'attestation' (061) is for DSPs
// with neither a badge nor LinkedIn: their own words, reviewed like the rest.
// The code comes first: it is the one instant path. Since open sign-up (064)
// codes no longer let people in, they only verify.
const verifyMethods = [
  {
    id: 'code',
    icon: Key,
    title: 'DSP Verification Code',
    description: 'Verifies you instantly',
    color: '#D4A03C',
  },
  {
    id: 'badge',
    icon: Camera,
    title: 'Work Badge Photo',
    description: 'Upload a photo of your agency ID or badge',
    color: '#E07A5F',
  },
  {
    id: 'linkedin',
    icon: Linkedin,
    title: 'LinkedIn Profile',
    description: 'Link your profile showing a DSP or caregiving role',
    color: '#0A66C2',
  },
  {
    id: 'attestation',
    icon: PenLine,
    title: 'Tell Us About Your Work',
    description: 'No badge or LinkedIn? Describe your DSP role',
    color: '#5B8C6F',
  },
];

// submit_verification_queue RAISEs readable messages for the cases a member
// can fix (061); anything else stays generic.
const QUEUE_ERRORS = [
  'Verification is for DSPs',
  'Already verified',
  'Tell us about your work',
  'Confirm your main role at work is as a DSP',
  'Note is too long',
  'Enter a LinkedIn profile URL',
];
const queueErrorMessage = (error) => {
  const known = QUEUE_ERRORS.find((m) => error?.message?.includes(m));
  return known ? `${error.message.replace(/\.$/, '')}.` : 'Something went wrong. Please try again.';
};

// onSubmitted fires when a request goes through (pending, or verified by a
// code) — onboarding uses it to move on rather than guessing from onClose.
// initialMethod opens straight on one method (onboarding's "I have a code").
const VerifyModal = ({ onClose, onSubmitted, initialMethod = null }) => {
  useModalDismiss(onClose);
  const { session, userData, refreshProfile } = useUser();
  const [selectedMethod, setSelectedMethod] = useState(initialMethod);
  const [inputValue, setInputValue] = useState('');
  const [badgeFile, setBadgeFile] = useState(null);
  const [badgePreview, setBadgePreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [note, setNote] = useState('');
  const [attested, setAttested] = useState(false);
  const fileInputRef = useRef(null);

  const rejectionReason = userData?.verification_rejection_reason;

  const handleBadgeSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ALLOWED_MIME.includes(file.type)) {
      setSubmitError('Please upload a JPEG, PNG, or WebP image.');
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setSubmitError('Image must be under 5MB.');
      return;
    }
    setSubmitError('');
    setBadgeFile(file);
    setBadgePreview(URL.createObjectURL(file));
  };

  const handleSubmit = async () => {
    if (!session?.user?.id) return;
    setSubmitting(true);
    setSubmitError('');

    if (selectedMethod === 'code') {
      await handleCodeSubmit();
    } else if (selectedMethod === 'badge') {
      await handleBadgeSubmit();
    } else if (selectedMethod === 'linkedin') {
      await handleLinkedInSubmit();
    } else if (selectedMethod === 'attestation') {
      await handleAttestationSubmit();
    }

    setSubmitting(false);
  };

  const handleCodeSubmit = async () => {
    const code = inputValue.trim();
    if (!code) {
      setSubmitError('Please enter your code.');
      return;
    }

    const { data, error } = await supabase.rpc('verify_by_invite_code', { p_code: code });

    if (error) {
      setSubmitError('Something went wrong. Please try again.');
      return;
    }
    if (data === 'invalid' || data === 'expired') {
      setSubmitError("That code isn't valid or has expired. Check for typos or ask your agency for a new code.");
      return;
    }
    // 058: the code is valid but doesn't verify — not marked to, or the member
    // chose a non-DSP role. The use is still counted.
    if (data === 'not_verifying') {
      setSubmitError("This code doesn't verify accounts. Please choose another way to verify.");
      return;
    }
    if (data === 'ok') {
      await refreshProfile();
      showSuccess("You're Verified!", 'Your DSP status has been confirmed. You now have full community access.');
    }
  };

  const handleBadgeSubmit = async () => {
    if (!badgeFile) {
      setSubmitError('Please select a photo to upload.');
      return;
    }

    const ext = badgeFile.type === 'image/png' ? 'png' : badgeFile.type === 'image/webp' ? 'webp' : 'jpg';
    const path = `${session.user.id}/${Date.now()}.${ext}`;

    const { error: uploadError } = await supabase.storage
      .from('verification-uploads')
      .upload(path, badgeFile, { contentType: badgeFile.type, upsert: false });

    if (uploadError) {
      setSubmitError('Upload failed. Please try again.');
      return;
    }

    const { error: rpcError } = await supabase.rpc('submit_verification_queue', {
      p_method: 'badge',
      p_data: path,
      p_note: note.trim() || null,
      p_attested: attested,
    });

    if (rpcError) {
      setSubmitError(queueErrorMessage(rpcError));
      return;
    }

    showSuccess('Badge Submitted', "We'll review your badge photo within 24 hours and notify you when it's approved.");
  };

  const handleLinkedInSubmit = async () => {
    const url = inputValue.trim();
    if (!url.toLowerCase().includes('linkedin.com/in/')) {
      setSubmitError('Please enter a valid LinkedIn profile URL (e.g. linkedin.com/in/yourname).');
      return;
    }

    const { error } = await supabase.rpc('submit_verification_queue', {
      p_method: 'linkedin',
      p_data: url,
      p_note: note.trim() || null,
      p_attested: attested,
    });

    if (error) {
      setSubmitError(queueErrorMessage(error));
      return;
    }

    showSuccess('LinkedIn Submitted', "We'll review your profile within 24 hours and notify you when it's approved.");
  };

  const handleAttestationSubmit = async () => {
    const { error } = await supabase.rpc('submit_verification_queue', {
      p_method: 'attestation',
      p_note: note.trim(),
      p_attested: attested,
    });

    if (error) {
      setSubmitError(queueErrorMessage(error));
      return;
    }

    showSuccess('Request Submitted', "Our team will read it and let you know when you're verified.");
  };

  const showSuccess = (title, message) => {
    setSuccessMessage({ title, message });
    setSuccess(true);
    // Every success changes verification_status (pending, or verified), and the
    // feed composer reads it — refresh so it says "Verification pending" now.
    refreshProfile();
    onSubmitted?.();
    setTimeout(() => onClose(), 3000);
  };

  // ── Success state ────────────────────────────────────────────
  if (success) {
    return (
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" onClick={onClose}>
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
        <div
          className="relative w-full max-w-md mx-4 mb-4 sm:mb-0 rounded-2xl p-8 text-center animate-fadeIn"
          style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--card-border)' }}
          onClick={e => e.stopPropagation()}
        >
          <div
            className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
            style={{ backgroundColor: 'var(--brand-primary)' }}
          >
            <CheckCircle size={32} className="text-white" />
          </div>
          <h3 className="text-xl font-bold text-primary mb-2">{successMessage.title}</h3>
          <p className="text-sm text-tertiary">{successMessage.message}</p>
        </div>
      </div>
    );
  }

  // ── Not a DSP role ───────────────────────────────────────────
  // Verification is for DSPs, so a non-DSP role gets the reason instead of
  // the methods. Every requireVerified() entry point lands here, so this one
  // check covers the feed, Tables, replies, comments and video submission.
  // UX only — an admin still decides every request (060 shows them the role).
  if (!isDspRole(userData?.role)) {
    return (
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" onClick={onClose}>
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
        <div
          className="relative w-full max-w-md mx-4 mb-4 sm:mb-0 rounded-2xl p-5 animate-fadeIn"
          style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--card-border)' }}
          onClick={e => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Shield size={20} style={{ color: 'var(--brand-primary)' }} />
              <h3 className="font-bold text-base text-primary">{DSP_CRITERION.headline}</h3>
            </div>
            <button onClick={onClose} aria-label="Close" className="p-1 rounded-lg hover-surface">
              <X size={20} style={{ color: 'var(--text-tertiary)' }} />
            </button>
          </div>
          <p className="text-sm text-primary">{DSP_CRITERION.qualifies}</p>
          <p className="text-xs text-tertiary mt-1.5">{DSP_CRITERION.notDsp}</p>
          <p className="text-xs text-tertiary mt-3">
            If your main role at work is as a DSP, change your job role on your profile first, then verify.
          </p>
          <button
            onClick={onClose}
            className="w-full mt-5 py-3 rounded-xl font-semibold text-white text-sm transition-all hover:brightness-110 active:scale-[0.98]"
            style={{ backgroundColor: 'var(--brand-primary)' }}
          >
            Got it
          </button>
        </div>
      </div>
    );
  }

  // ── Method detail view ───────────────────────────────────────
  if (selectedMethod) {
    const method = verifyMethods.find(m => m.id === selectedMethod);
    const Icon = method.icon;

    const placeholders = {
      code: 'Enter your code',
      linkedin: 'linkedin.com/in/yourprofile',
    };

    const reviewed = REVIEWED_METHODS.includes(method.id);
    const isSubmitDisabled = () => {
      if (submitting) return true;
      if (reviewed && !attested) return true;
      if (note.length > VERIFICATION_NOTE_MAX) return true;
      if (method.id === 'badge') return !badgeFile;
      if (method.id === 'attestation') return !note.trim();
      return !inputValue.trim();
    };

    return (
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" onClick={onClose}>
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
        <div
          className="relative w-full max-w-md mx-4 mb-4 sm:mb-0 rounded-2xl overflow-y-auto max-h-[90vh] animate-fadeIn"
          style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--card-border)' }}
          onClick={e => e.stopPropagation()}
        >
          <div className="px-5 pt-5 pb-4">
            {/* Nav row */}
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => { setSelectedMethod(null); setInputValue(''); setBadgeFile(null); setBadgePreview(null); setNote(''); setAttested(false); setSubmitError(''); }}
                className="text-sm text-tertiary hover:text-primary transition-colors"
              >
                &larr; Back
              </button>
              <button onClick={onClose} aria-label="Close" className="p-1 rounded-lg hover-surface">
                <X size={20} style={{ color: 'var(--text-tertiary)' }} />
              </button>
            </div>

            {/* Method header */}
            <div className="flex items-center gap-3 mb-4">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: method.color }}
              >
                <Icon size={20} className="text-white" />
              </div>
              <div>
                <h3 className="font-bold text-base text-primary">{method.title}</h3>
                <p className="text-xs text-tertiary">{method.description}</p>
              </div>
            </div>

            {/* Input area */}
            {method.id === 'badge' ? (
              <>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={handleBadgeSelect}
                />
                {badgePreview ? (
                  <div className="relative rounded-xl overflow-hidden mb-4" style={{ aspectRatio: '16/9' }}>
                    <img src={badgePreview} alt="Badge preview" className="w-full h-full object-cover" />
                    <button
                      onClick={() => { setBadgeFile(null); setBadgePreview(null); }}
                      className="absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center bg-black/60"
                    >
                      <X size={14} className="text-white" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full rounded-xl p-6 text-center transition-all hover-surface mb-4"
                    style={{ border: '2px dashed var(--card-border)', backgroundColor: 'var(--surface-hover)' }}
                  >
                    <Camera size={32} style={{ color: 'var(--text-tertiary)' }} className="mx-auto mb-2" />
                    <p className="text-sm text-tertiary">Tap to upload a photo of your badge or ID</p>
                    <p className="text-xs text-tertiary mt-1">JPEG, PNG, WebP · Max 5MB · Encrypted, used for verification only</p>
                  </button>
                )}
                {badgeFile && (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="text-xs mb-3 block"
                    style={{ color: 'var(--brand-primary)' }}
                  >
                    Change photo
                  </button>
                )}
              </>
            ) : method.id === 'attestation' ? null : (
              <input
                type="text"
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                placeholder={placeholders[method.id]}
                className="w-full px-4 py-3 rounded-xl text-sm text-primary focus:outline-none focus:ring-2 focus:ring-brand-secondary mb-4"
                style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)' }}
                autoFocus
              />
            )}

            {/* 061: the member's own words — the evidence itself for
                'attestation', optional context on a badge or LinkedIn — and
                the tick, which every reviewed request needs. */}
            {reviewed && (
              <>
                <label htmlFor="verify-note" className="block text-xs font-medium text-secondary mb-1.5">
                  {method.id === 'attestation'
                    ? 'Tell us about your work'
                    : <>Anything you'd like us to know? <span className="text-tertiary font-normal">(optional)</span></>}
                </label>
                <textarea
                  id="verify-note"
                  value={note}
                  onChange={e => setNote(e.target.value)}
                  rows={method.id === 'attestation' ? 5 : 3}
                  placeholder={method.id === 'attestation'
                    ? 'Where do you work, how long have you been a DSP, and what does a typical shift look like?'
                    : 'For example, where you work or why your badge looks the way it does'}
                  className="w-full px-4 py-3 rounded-xl text-sm text-primary focus:outline-none focus:ring-2 focus:ring-brand-secondary resize-none"
                  style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)' }}
                  autoFocus={method.id === 'attestation'}
                />
                <p
                  className="text-xs text-right mt-1 mb-3"
                  style={{ color: note.length > VERIFICATION_NOTE_MAX ? 'var(--color-error)' : 'var(--text-tertiary)' }}
                >
                  {note.length}/{VERIFICATION_NOTE_MAX}
                </p>
                <label className="flex items-start gap-2.5 mb-4 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={attested}
                    onChange={e => setAttested(e.target.checked)}
                    className="mt-0.5 w-4 h-4 flex-shrink-0"
                    style={{ accentColor: 'var(--brand-primary)' }}
                  />
                  <span className="text-sm text-primary">
                    I confirm my main role at work is as a Direct Support Professional.
                  </span>
                </label>
              </>
            )}

            {submitError && (
              <p className="text-xs text-center mb-3" style={{ color: 'var(--color-error)' }}>
                {submitError}
              </p>
            )}

            <button
              onClick={handleSubmit}
              disabled={isSubmitDisabled()}
              className="w-full py-3 rounded-xl font-semibold text-white text-sm transition-all duration-200 hover:brightness-110 active:scale-[0.98] disabled:opacity-40 flex items-center justify-center gap-2"
              style={{ backgroundColor: method.color }}
            >
              {submitting ? (
                <>
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {method.id === 'code' ? 'Checking...' : 'Submitting...'}
                </>
              ) : method.id === 'code' ? (
                'Apply Code'
              ) : (
                'Submit for Review'
              )}
            </button>

            <p className="text-xs text-tertiary text-center mt-3">
              {method.id === 'code' && 'Instant verification — no review needed'}
              {(method.id === 'badge' || method.id === 'linkedin') && 'Review typically takes less than 24 hours'}
              {method.id === 'attestation' && 'A team member reads every request'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Method selection view ────────────────────────────────────
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-md mx-4 mb-4 sm:mb-0 rounded-2xl overflow-hidden animate-fadeIn"
        style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--card-border)' }}
        onClick={e => e.stopPropagation()}
      >
        <div className="px-5 pt-5 pb-3">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <Shield size={20} style={{ color: 'var(--brand-primary)' }} />
              <h3 className="font-bold text-base text-primary">Verify Your DSP Status</h3>
            </div>
            <button onClick={onClose} aria-label="Close" className="p-1 rounded-lg hover-surface">
              <X size={20} style={{ color: 'var(--text-tertiary)' }} />
            </button>
          </div>

          {/* Rejection notice — shown if a previous submission was rejected */}
          {rejectionReason && (
            <div
              className="flex items-start gap-2 rounded-xl p-3 mt-3 mb-1"
              style={{ backgroundColor: '#FEF3C7', border: '1px solid #F59E0B' }}
            >
              <AlertTriangle size={15} className="mt-0.5 flex-shrink-0" style={{ color: '#92400E' }} />
              <p className="text-xs" style={{ color: '#92400E' }}>
                <span className="font-semibold">Previous submission not approved:</span> {rejectionReason}
                {' '}You're welcome to try again.
              </p>
            </div>
          )}

          <p className="text-sm text-primary mt-3">{DSP_CRITERION.qualifies}</p>
          <p className="text-xs text-tertiary mt-1.5 mb-4">
            {DSP_CRITERION.unlocks} Choose one way to verify.
          </p>
        </div>

        <div className="px-5 pb-5 space-y-2">
          {verifyMethods.map(method => {
            const Icon = method.icon;
            return (
              <button
                key={method.id}
                onClick={() => setSelectedMethod(method.id)}
                className="w-full p-3.5 rounded-xl flex items-center gap-3 transition-all duration-200 hover:-translate-y-0.5 active:scale-[0.98]"
                style={{ backgroundColor: 'var(--pill-bg)', border: '1px solid var(--pill-border)' }}
              >
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: method.color }}
                >
                  <Icon size={18} className="text-white" />
                </div>
                <div className="flex-1 text-left min-w-0">
                  <div className="font-semibold text-sm text-primary">{method.title}</div>
                  <div className="text-xs text-tertiary truncate">{method.description}</div>
                </div>
                <ArrowRight size={16} style={{ color: 'var(--text-tertiary)' }} className="flex-shrink-0" />
              </button>
            );
          })}
        </div>

        <div className="px-5 pb-4">
          <p className="text-xs text-tertiary text-center">
            Having trouble? Contact <span style={{ color: 'var(--brand-primary)' }}>hello@dsp.cafe</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default VerifyModal;
